import React from 'react';
import ProgressBar from '@magoosh/lib/progress_bar';
import { PrimaryButton, SecondaryButton } from '@magoosh/lib/button';
import { FontAwesomeIcon } from '@magoosh/lib/icons';
import { Col, Row } from 'react-bootstrap';
import style from './style.module.scss';
import cx from 'classnames';
import { pluralize } from 'utilities/pluralize';
import { createMarking, goToNextUrl } from './utils';
import PracticeSessionPreviewModal from '@magoosh/organisms/study_schedule/practice_session_preview_modal/template';
import { StudyItemData } from '@magoosh/organisms/study_items/types';
import RatingModal from '@magoosh/organisms/rating_modal';
import CustomizePracticeFormModal from '@magoosh/gre/adaptive_prep_v1/organisms/customize_practice_form_modal';
import { paths } from 'config/path_helpers';

export interface IntraModuleNavProps {
  authenticityToken: string;
  guidedActivity: {
    id: number;
    name: string;
    url: string;
    type: string;
    assignmentId?: number;
  };
  currentStudyItem: {
    name: string;
    lessonId?: number;
  };
  nextStudyItem: StudyItemData & {
    text?: 'none' | 'next' | 'remaining';
    questionCount?: number;
    questionType?: 'question' | 'task';
  };
  remainingItemCount: number;
  totalItemCount: number;
  allowNavigation: boolean;
}

export const IntraModuleNav: React.FC<IntraModuleNavProps> = (props) => {
  const [isMobile, setIsMobile] = React.useState(false);
  const [showRatingModal, setShowRatingModal] = React.useState(false);
  const [practicePreview, setPracticePreview] = React.useState(null);

  React.useEffect(() => {
    setIsMobile(window.innerWidth < 768);

    window.addEventListener('resize', () => {
      if (window.innerWidth < 768 && !isMobile) {
        setIsMobile(true);
      } else if (window.innerWidth > 768 && isMobile) {
        setIsMobile(false);
      }
    });
  });

  const handleContinueOrFinishClick = () => {
    let resultingAction;

    if (props.remainingItemCount === 0) {

      if (props.guidedActivity.type == 'ContentBlock::SectionedAssignmentBlock' ||
        props.guidedActivity.assignmentId) {
        fetch(paths.api.submitAssignment(props.guidedActivity.assignmentId), { method: 'put' })
          .then();
      }

      resultingAction = () => setShowRatingModal(true);
    } else {
      resultingAction = () => goToNextUrl(props, setPracticePreview);
    }

    if (props.currentStudyItem.lessonId) {
      createMarking(props.currentStudyItem.lessonId, props.authenticityToken).then(() => {
        resultingAction();
      });
    } else {
      resultingAction();
    }
  };

  const practicePreviewModal = () => {
    if (props.nextStudyItem.type === 'Practice') {
      return <PracticeSessionPreviewModal onHide={() => setPracticePreview(null)} {...practicePreview} />;
    } else if (props.nextStudyItem.type === 'Slice Practice') {
      return (
        <CustomizePracticeFormModal open toggleOpen={() => setPracticePreview(null)} {...practicePreview} />
      );
    }
  };

  return (
    <>
      <Row className={cx(style.intraModuleNav, 'u-padding-V-m')}>
        <Col xs={12} md={5} className={style.currentItemSection}>
          <div className={cx(style.currentItemText, 'u-margin-V-n u-margin-L-n u-margin-R-xs')}>
            <a style={{ color: style.grayText, textDecoration: 'none' }} href={props.guidedActivity.url}>
              {props.guidedActivity.name}
            </a>
            <div className={style.progressBar}>
              <ProgressBar
                backgroundColor="#cef0e3"
                color="#26B67C"
                percentage={((props.totalItemCount - props.remainingItemCount) / props.totalItemCount) * 100}
                size="sm"
              />
            </div>
          </div>
          <h3 className="u-margin-A-n">{props.currentStudyItem.name}</h3>
        </Col>
        <Col xs={12} md={7} className={style.nextItemSection}>
          {props.allowNavigation && (
            <>
              {props.remainingItemCount > 0 && (
                <>
                  <div className={style.nextItemText}>
                    <p className="u-margin-V-n u-margin-L-n u-margin-R-xs">
                      {props.nextStudyItem.text === 'next' ? 'Up Next:' : 'Remaining:'}
                    </p>
                    <h4 className="u-margin-V-n u-margin-L-n u-margin-R-xs">
                      <strong>
                        {['Practice', 'Slice Practice', 'Adaptive Slice Practice'].indexOf(
                          props.nextStudyItem.type
                        ) > -1 ? (
                          <FontAwesomeIcon icon={'list-alt'} />
                        ) : (
                          <FontAwesomeIcon icon={'play-circle'} />
                        )}
                        {'  '}
                        {isMobile ? (
                          <a
                            className="u-margin-V-n u-margin-L-n u-margin-R-xs"
                            style={{ color: style.brandSecondary }}
                            onClick={handleContinueOrFinishClick}>
                            {props.nextStudyItem.name}{' '}
                            {props.nextStudyItem.questionCount &&
                              props.nextStudyItem.questionType &&
                              `(${pluralize(
                                props.nextStudyItem.questionCount,
                                props.nextStudyItem.questionType
                              )})`}
                          </a>
                        ) : (
                          <>
                            {props.nextStudyItem.name}{' '}
                            {props.nextStudyItem.questionCount &&
                              props.nextStudyItem.questionType &&
                              `(${pluralize(
                                props.nextStudyItem.questionCount,
                                props.nextStudyItem.questionType
                              )})`}
                          </>
                        )}
                      </strong>
                    </h4>
                    {props.nextStudyItem.text === 'remaining' && props.remainingItemCount > 1 && !isMobile && (
                      <a
                        className="u-margin-V-n u-margin-L-n u-margin-R-xs"
                        style={{ color: style.brandSecondary }}
                        href={props.guidedActivity.url}>
                        <strong>{`+ ${props.remainingItemCount - 1} more`}</strong>
                      </a>
                    )}
                  </div>
                  {!isMobile && (
                    <PrimaryButton submitting={false} onClick={handleContinueOrFinishClick}>
                      Continue
                    </PrimaryButton>
                  )}
                </>
              )}

              {props.remainingItemCount === 0 && (
                <>
                  <h4 className="u-margin-V-n u-margin-L-n u-margin-R-xs">
                    <strong>
                      <FontAwesomeIcon icon={'check-circle'} className={style.greenCheck} />
                      {'  '}
                      {`${props.totalItemCount} of ${props.totalItemCount} Activities`}
                    </strong>
                  </h4>
                  <SecondaryButton submitting={false} onClick={handleContinueOrFinishClick}>
                    {props.guidedActivity.assignmentId ? 'Complete Assignment' : 'Finish'}
                  </SecondaryButton>
                </>
              )}
            </>
          )}
        </Col>
      </Row>

      {practicePreview && practicePreviewModal()}

      <RatingModal
        rateableId={props.guidedActivity.id}
        open={showRatingModal}
        onHide={() => setShowRatingModal(false)}
      />
    </>
  );
};

export default IntraModuleNav;
